import { useState, type FormEvent } from "react";
import { useNavigate } from "@tanstack/react-router";
import { ApiError, retryAfterSeconds } from "@/api/client";
import { useMeta, useSession, useUnlock } from "@/api/queries";
import { BrandMark } from "@/components/BrandMark";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/Button";
import { Field } from "@/components/ui/Field";
import { AlertIcon, ArrowRightIcon, CheckIcon, KeyIcon } from "@/components/icons";
import { useBrand } from "@/theme/context";

const HIGHLIGHTS = [
  "Ask questions in plain English",
  "Every answer backed by read-only SQL you can inspect",
  "Good answers become refreshable reports and dashboards",
];

function describeError(error: unknown): string {
  if (error instanceof ApiError) {
    const retryAfter = retryAfterSeconds(error);
    if (retryAfter !== null) {
      const minutes = Math.ceil(retryAfter / 60);
      return `Too many attempts. Try again in about ${minutes} minute${minutes === 1 ? "" : "s"}.`;
    }
    if (error.code === "invalid_access_key" || error.code === "invalid_credentials") {
      const detail = error.detail as { attempts_remaining?: number } | undefined;
      const remaining = detail?.attempts_remaining;
      return remaining !== undefined
        ? `${error.message} ${remaining} attempt${remaining === 1 ? "" : "s"} left before a timeout.`
        : error.message;
    }
    return error.message;
  }
  return "Something went wrong. Please try again.";
}

export function UnlockPage() {
  const { productName, tagline, welcomeBody } = useBrand();
  const { data: meta } = useMeta();
  const unlock = useUnlock();
  const navigate = useNavigate();
  const { data: session } = useSession();
  const [accessKey, setAccessKey] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  // Once anybody has an account, signing in by name is the front door; the
  // key stays one click away, because it still works (ACC-3).
  const [preferKey, setPreferKey] = useState(false);
  const byName = Boolean(session?.accounts_enabled) && !preferKey;
  const ready = byName ? Boolean(username.trim() && password) : Boolean(accessKey.trim());

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!ready) return;
    unlock.mutate(byName ? { username: username.trim(), password } : { access_key: accessKey }, {
      onSuccess: () => {
        setAccessKey("");
        setPassword("");
        void navigate({ to: "/" });
      },
    });
  };

  return (
    <div className="grid min-h-dvh grid-cols-1 bg-bg lg:grid-cols-[1.05fr_1fr]">
      {/* Brand panel */}
      <section className="relative hidden flex-col justify-between overflow-hidden border-e border-border bg-surface p-12 lg:flex">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(120% 90% at 12% 8%, var(--ming-accent-subtle) 0%, transparent 62%)",
          }}
        />
        <div className="relative flex items-center gap-3">
          <BrandMark size={34} />
          <span className="type-h2 text-ink">{productName}</span>
        </div>

        <div className="relative max-w-lg">
          <h1 className="type-display text-ink text-balance">
            Chat with your data, and trust the answer.
          </h1>
          <div className="mt-6 mb-7 h-px w-16 bg-accent" />
          <ul className="flex flex-col gap-4">
            {HIGHLIGHTS.map((highlight) => (
              <li key={highlight} className="flex items-start gap-3">
                <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-accent-subtle text-accent">
                  <CheckIcon className="size-3" />
                </span>
                <span className="type-body text-ink-secondary">{highlight}</span>
              </li>
            ))}
          </ul>
        </div>

        <p className="relative type-support text-ink-disabled">
          {/* The client's own words when they wrote any (PRD FR-11.1). The
              unlock page is the first thing anybody sees, and it is the surface
              a rebrand most obviously has to reach. */}
          {welcomeBody || tagline}
          {meta ? ` · ${meta.product_name} v${meta.version}` : null}
        </p>
      </section>

      {/* Unlock panel */}
      <section className="flex flex-col p-6 sm:p-10">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 lg:hidden">
            <BrandMark size={30} />
            <span className="type-h3 text-ink">{productName}</span>
          </div>
          <div className="ms-auto">
            <ThemeToggle />
          </div>
        </div>

        <div className="flex flex-1 items-center justify-center py-10">
          <div className="w-full max-w-sm">
            <span className="inline-flex size-11 items-center justify-center rounded-container bg-accent-subtle text-accent">
              <KeyIcon className="size-5" />
            </span>
            <h2 className="type-h1 text-ink mt-5">{byName ? "Sign in" : "Unlock"}</h2>
            <p className="type-body text-ink-secondary mt-2">
              {byName
                ? "Sign in with the account your administrator made for you."
                : "This deployment is shared. Enter the access key to continue."}
            </p>

            <form onSubmit={onSubmit} className="mt-8 flex flex-col gap-5" noValidate>
              {byName ? (
                <>
                  <Field
                    label="Username"
                    autoFocus
                    autoComplete="username"
                    spellCheck={false}
                    autoCapitalize="none"
                    value={username}
                    onChange={(event) => setUsername(event.target.value)}
                  />
                  <Field
                    label="Password"
                    revealable
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    error={unlock.isError ? describeError(unlock.error) : undefined}
                  />
                </>
              ) : (
              <Field
                label="Access key"
                revealable
                autoFocus
                autoComplete="current-password"
                spellCheck={false}
                placeholder="Enter your access key"
                value={accessKey}
                onChange={(event) => setAccessKey(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    event.currentTarget.form?.requestSubmit();
                  }
                }}
                error={unlock.isError ? describeError(unlock.error) : undefined}
                hint="Your administrator can give you one."
              />
              )}
              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="w-full"
                loading={unlock.isPending}
                disabled={!ready}
              >
                {byName ? "Sign in" : "Unlock"}
                {!unlock.isPending ? <ArrowRightIcon className="size-4" /> : null}
              </Button>
              {session?.accounts_enabled ? (
                <button
                  type="button"
                  className="type-support text-accent self-start hover:underline"
                  onClick={() => {
                    unlock.reset();
                    setPreferKey((value) => !value);
                  }}
                >
                  {byName ? "Use an access key instead" : "Sign in with a username instead"}
                </button>
              ) : null}
            </form>

            {meta && !unlock.isError ? (
              <p className="type-support text-ink-disabled mt-6 flex items-start gap-2">
                <AlertIcon className="size-4 shrink-0" />
                <span>
                  Attempts are rate-limited and recorded in the audit log. Nothing you type here is
                  ever written to a log.
                </span>
              </p>
            ) : null}
          </div>
        </div>
      </section>
    </div>
  );
}
